import { useCallback, useEffect, useRef } from 'react';
import type { Frame } from '@/game/view/frame';
import { labelPosition, type WorkTarget, workTargetAt } from '@/game/view/workTarget';
import { useSimSelector } from './app/simFrame';
import { type UiStore, useUi } from './ui/store';

const sameTarget = (a: WorkTarget | null, b: WorkTarget | null) =>
  a === b || (!!a && !!b && a.kind === b.kind && a.icon === b.icon && a.text === b.text);

/**
 * While the selection holds workers and the cursor is over something they
 * can work (a right-click would assign them): a gather or build cursor on
 * the map (`body[data-work-cursor]`, index.css) and a label with the target's
 * name and slots following the pointer. Re-renders only when the target or
 * its label changes.
 */
export function WorkHint({ ui }: { ui: UiStore }) {
  const hoveredKey = useUi(ui, (s) => s.hoveredKey);
  const selected = useUi(ui, (s) => s.selectedUnitIds);
  // Not while placing, targeting or box-selecting.
  const armed = useUi(
    ui,
    (s) => s.placementMode !== null || s.targetMode !== null || s.marquee !== null
  );
  const select = useCallback(
    (frame: Frame) => {
      if (!hoveredKey || armed) return null;
      const [q, r] = hoveredKey.split(',').map(Number);
      return workTargetAt(frame, selected, { q, r });
    },
    [hoveredKey, selected, armed]
  );
  const target = useSimSelector(select, sameTarget);
  const label = useRef<HTMLDivElement>(null);
  const pointer = useRef({ x: 0, y: 0 });

  // The label tracks the pointer without re-rendering, kept inside the window.
  const place = useCallback(() => {
    const el = label.current;
    if (!el) return;
    const { x, y } = labelPosition(
      pointer.current,
      { w: el.offsetWidth, h: el.offsetHeight },
      { w: window.innerWidth, h: window.innerHeight }
    );
    el.style.transform = `translate(${x}px, ${y}px)`;
  }, []);
  useEffect(() => {
    const move = (e: PointerEvent) => {
      pointer.current = { x: e.clientX, y: e.clientY };
      place();
    };
    window.addEventListener('pointermove', move);
    return () => window.removeEventListener('pointermove', move);
  }, [place]);
  const kind = target?.kind;
  useEffect(() => {
    if (!kind) return;
    document.body.dataset.workCursor = kind;
    return () => {
      delete document.body.dataset.workCursor;
    };
  }, [kind]);

  if (!target) return null;
  return (
    <div
      ref={(el) => {
        label.current = el;
        place();
      }}
      className="pill pointer-events-none fixed top-0 left-0 z-20 px-2.5 py-1 text-xs whitespace-nowrap"
    >
      {target.icon} {target.text}
    </div>
  );
}
